import { supabase } from "@/integrations/supabase/client";

/**
 * Find an existing direct conversation between two people, or create one.
 * Returns the conversation id.
 */
export async function getOrCreateDirectConversation(myId: string, otherId: string): Promise<string> {
  if (!myId) throw new Error("You need to be signed in to send messages.");
  if (!otherId) throw new Error("We could not find that person.");
  if (myId === otherId) throw new Error("You cannot message yourself.");

  const [{ data: mine }, { data: theirs }] = await Promise.all([
    supabase.from("conversation_members").select("conversation_id").eq("user_id", myId),
    supabase.from("conversation_members").select("conversation_id").eq("user_id", otherId),
  ]);

  const theirIds = new Set((theirs ?? []).map((r) => r.conversation_id));
  const shared = (mine ?? []).map((r) => r.conversation_id).filter((id) => theirIds.has(id));

  if (shared.length) {
    const { data: existing } = await supabase
      .from("conversations")
      .select("id")
      .in("id", shared)
      .eq("conv_type", "direct")
      .order("last_message_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (existing) return existing.id;
  }

  const { data: conv, error } = await supabase
    .from("conversations")
    .insert({ created_by: myId, conv_type: "direct" })
    .select("id")
    .single();
  if (error || !conv) throw new Error(error?.message ?? "Could not start this conversation.");

  const { error: memberError } = await supabase.from("conversation_members").insert([
    { conversation_id: conv.id, user_id: myId, role: "owner" },
    { conversation_id: conv.id, user_id: otherId },
  ]);
  if (memberError) throw new Error(memberError.message);

  return conv.id;
}

/** Resolve a username to a user id, then open or create the direct conversation. */
export async function getOrCreateDirectConversationByUsername(myId: string, username: string): Promise<string> {
  const { data: other } = await supabase
    .from("profiles")
    .select("id")
    .eq("username", username.trim())
    .maybeSingle();
  if (!other) throw new Error("We could not find that person.");
  return getOrCreateDirectConversation(myId, other.id);
}
